import { useCallback, useState } from "react";
import {
  Card,
  BlockStack,
  Text,
  FormLayout,
  DropZone,
  Box,
  Button,
  InlineStack,
  Banner,
  List,
  Spinner,
  InlineGrid,
  Checkbox,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { GenerativeTextField } from "@shared/ui";
import { WebsiteField } from "@shared/ui";
import { Brand, ImageDetailType } from "@features/events/types";

interface BrandingMediaCardProps {
  brandData: Partial<Brand>;
  onDataChange: (updates: Partial<Brand>) => void;
  fieldErrors: Record<string, string>;
}

export default function BrandingMediaCard({
  brandData,
  onDataChange,
  fieldErrors,
}: BrandingMediaCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { POST } = useApiRequest();

  const [rejectedRectangleFiles, setRejectedRectangleFiles] = useState<File[]>([]);
  const [rejectedSquareFiles, setRejectedSquareFiles] = useState<File[]>([]);
  const [showProfileButtonOverride, setShowProfileButtonOverride] = useState<
    boolean | null
  >(null);
  const showProfileButton =
    showProfileButtonOverride ?? Boolean(brandData.customButtonLabel);

  // Upload logoRectangle mutation
  const { mutateAsync: uploadLogoRectangle, isPending: isUploadingRectangle } =
    useMutation({
      mutationFn: async (file: File): Promise<ImageDetailType[]> => {
        const formData = new FormData();
        formData.append("file", file);

        if (!hcc || !brandData.id) {
          throw new Error("HCC and Brand ID are required for logo upload");
        }

        return POST(
          `/api/healthcare-companies/${hcc}/e/brands/${brandData.id}/logo/rectangle`,
          formData
        );
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["my-brand", hcc],
        });
        onDataChange({ logoRectangle: data });
        setRejectedRectangleFiles([]);
      },
    });

  // Upload logoSquare mutation
  const { mutateAsync: uploadLogoSquare, isPending: isUploadingSquare } =
    useMutation({
      mutationFn: async (file: File): Promise<ImageDetailType[]> => {
        const formData = new FormData();
        formData.append("file", file);

        if (!hcc || !brandData.id) {
          throw new Error("HCC and Brand ID are required for logo upload");
        }

        return POST(
          `/api/healthcare-companies/${hcc}/e/brands/${brandData.id}/logo/square`,
          formData
        );
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["my-brand", hcc],
        });
        onDataChange({ logoSquare: data });
        setRejectedSquareFiles([]);
      },
    });


  const handleLogoRectangleDrop = useCallback(
    (
      _droppedFiles: File[],
      acceptedFiles: File[],
      rejectedFiles: File[]
    ) => {
      if (acceptedFiles.length > 0) {
        uploadLogoRectangle(acceptedFiles[0]);
      }
      setRejectedRectangleFiles(rejectedFiles);
    },
    [uploadLogoRectangle]
  );

  const handleLogoSquareDrop = useCallback(
    (
      _droppedFiles: File[],
      acceptedFiles: File[],
      rejectedFiles: File[]
    ) => {
      if (acceptedFiles.length > 0) {
        uploadLogoSquare(acceptedFiles[0]);
      }
      setRejectedSquareFiles(rejectedFiles);
    },
    [uploadLogoSquare]
  );

  const handleRemoveLogoRectangle = useCallback(() => {
    onDataChange({ logoRectangle: undefined });
  }, [onDataChange]);

  const handleRemoveLogoSquare = useCallback(() => {
    onDataChange({ logoSquare: undefined });
  }, [onDataChange]);

  const handleShowProfileButtonChange = useCallback(
    (value: boolean) => {
      setShowProfileButtonOverride(value);
      if (!value) {
        // When disabling, clear both fields
        onDataChange({
          customButtonLink: undefined,
          customButtonLabel: undefined,
        });
      }
    },
    [onDataChange]
  );

  const handleCustomButtonLinkChange = useCallback(
    (value: string) => {
      // Only update if profile button is enabled
      if (showProfileButton) {
        onDataChange({ customButtonLink: value });
      }
    },
    [onDataChange, showProfileButton]
  );

  const logoRectangleUrl = brandData.logoRectangle?.[0]?.src;
  const logoSquareUrl = brandData.logoSquare?.[0]?.src;

  const hasRectangleError = rejectedRectangleFiles.length > 0;
  const hasSquareError = rejectedSquareFiles.length > 0;

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("brandProfile.branding.title")}
        </Text>

        <Text as="p" variant="bodyMd" tone="subdued">
          {t("brandProfile.branding.subtitle")}
        </Text>

        <FormLayout>
          {/* Logo Uploads Side by Side */}
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd" fontWeight="medium">
              {t("brandProfile.branding.logos")}
            </Text>
            
            <InlineGrid columns={2} gap="400">
              {/* Logo Rectangle Upload */}
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  {t("brandProfile.branding.logoRectangle")}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("brandProfile.branding.logoRectangleHelp")}
                </Text>

                {hasRectangleError && (
                  <Banner tone="critical" title={t("brandProfile.branding.uploadError")}>
                    <List type="bullet">
                      {rejectedRectangleFiles.map((file, index) => (
                        <List.Item key={file.name + index}>
                          {t("brandProfile.branding.uploadErrorItem", {
                            fileName: file.name,
                          })}
                        </List.Item>
                      ))}
                    </List>
                  </Banner>
                )}

                {isUploadingRectangle ? (
                  <Box padding="400">
                    <InlineStack align="center">
                      <Spinner size="small" />
                    </InlineStack>
                  </Box>
                ) : logoRectangleUrl ? (
                  <BlockStack gap="200">
                    <Box>
                      <img
                        src={logoRectangleUrl}
                        alt={t("brandProfile.branding.logoRectangle")}
                        style={{ width: "100%", borderRadius: "4px", border: "1px solid #e1e3e5" }}
                      />
                    </Box>
                    <Box>
                      <Button
                        onClick={handleRemoveLogoRectangle}
                        variant="plain"
                        tone="critical"
                        size="slim"
                      >
                        {t("brandProfile.branding.removeLogo")}
                      </Button>
                    </Box>
                  </BlockStack>
                ) : (
                  <DropZone
                    accept="image/*"
                    type="image"
                    allowMultiple={false}
                    onDrop={handleLogoRectangleDrop}
                  >
                    <DropZone.FileUpload
                      actionTitle={t("brandProfile.branding.uploadAction")}
                    />
                  </DropZone>
                )}
              </BlockStack>

              {/* Logo Square Upload */}
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  {t("brandProfile.branding.logoSquare")}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("brandProfile.branding.logoSquareHelp")}
                </Text>

                {hasSquareError && (
                  <Banner tone="critical" title={t("brandProfile.branding.uploadError")}>
                    <List type="bullet">
                      {rejectedSquareFiles.map((file, index) => (
                        <List.Item key={file.name + index}>
                          {t("brandProfile.branding.uploadErrorItem", {
                            fileName: file.name,
                          })}
                        </List.Item>
                      ))}
                    </List>
                  </Banner>
                )}

                {isUploadingSquare ? (
                  <Box padding="400">
                    <InlineStack align="center">
                      <Spinner size="small" />
                    </InlineStack>
                  </Box>
                ) : logoSquareUrl ? (
                  <BlockStack gap="200">
                    <Box>
                      <img
                        src={logoSquareUrl}
                        alt={t("brandProfile.branding.logoSquare")}
                        style={{ width: "100%", borderRadius: "4px", border: "1px solid #e1e3e5" }}
                      />
                    </Box>
                    <Box>
                      <Button
                        onClick={handleRemoveLogoSquare}
                        variant="plain"
                        tone="critical"
                        size="slim"
                      >
                        {t("brandProfile.branding.removeLogo")}
                      </Button>
                    </Box>
                  </BlockStack>
                ) : (
                  <DropZone
                    accept="image/*"
                    type="image"
                    allowMultiple={false}
                    onDrop={handleLogoSquareDrop}
                  >
                    <DropZone.FileUpload
                      actionTitle={t("brandProfile.branding.uploadAction")}
                    />
                  </DropZone>
                )}
              </BlockStack>
            </InlineGrid>
          </BlockStack>

          {/* Custom Profile Button */}
          <BlockStack gap="300">
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                {t("brandProfile.branding.profileButton.title")}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {t("brandProfile.branding.profileButton.description")}
              </Text>
            </BlockStack>

            <Checkbox
              label={t("brandProfile.branding.profileButton.showButton")}
              checked={showProfileButton}
              onChange={handleShowProfileButtonChange}
            />

            {showProfileButton && (
              <FormLayout>
                <GenerativeTextField
                  id="customButtonLabel"
                  placementId="event.brand.customButtonLabel"
                  label={t("brandProfile.branding.profileButton.buttonLabel")}
                  value={brandData.customButtonLabel ?? {}}
                  onChange={(value) => onDataChange({ customButtonLabel: value })}
                  type="textField"
                  canGenerate={true}
                  requiredIndicator
                />
                <WebsiteField
                  label={t("brandProfile.branding.profileButton.linkUrl")}
                  value={brandData.customButtonLink || ""}
                  onChange={handleCustomButtonLinkChange}
                  error={fieldErrors.customButtonLink}
                  placeholder={t("common.placeholders.url")}
                />
              </FormLayout>
            )}
          </BlockStack>
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
